import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";

export const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user, loading: authLoading } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchParams = new URLSearchParams(location.search);
  const fromQuery = searchParams.get("from");
  const fromState = (location.state as { from?: string } | null)?.from;
  const from = fromQuery ?? fromState ?? "/";

  useEffect(() => {
    if (!authLoading && user) {
      navigate(from, { replace: true });
    }
  }, [user, authLoading, from, navigate]);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (!email || !password) {
      return;
    }
    setLoading(true);
    try {
      await login(email, password, rememberMe);
      navigate(from, { replace: true });
    } catch {
      setError("Credenciales incorrectas");
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-primary">
        <Skeleton className="h-12 w-64" />
      </div>
    );
  }

  return (
    <div className="login-wrapper flex min-h-screen items-stretch bg-bg-primary">
      <aside className="login-left hidden p-12 lg:flex lg:flex-1 lg:flex-col lg:items-center lg:justify-center">
        <img
          src="/gydconsulting.svg"
          alt="G&amp;D Consulting Group"
          className="img-logo h-auto w-full max-w-[450px] object-contain"
        />
      </aside>

      <main className="login-right flex flex-1 items-center justify-center p-6 sm:p-8">
        <div className="login-form-container w-full max-w-md">
          <div className="mb-6 text-center lg:hidden">
            <img
              src="/gydconsulting.svg"
              alt="G&D Consulting Group"
              className="img-logo mx-auto w-full max-w-[250px] object-contain"
            />
          </div>

          <h2 className="login-title text-center text-2xl font-semibold text-ink-primary">
            Iniciar sesión
          </h2>

          <form
            onSubmit={onSubmit}
            className="mt-6 space-y-4"
            noValidate
          >
            <div className="form-floating relative">
              <input
                aria-describedby="usernameError"
                autoComplete="username"
                autoFocus
                className="form-control peer w-full rounded-lg border border-slate-200 bg-white px-3 py-3 text-sm text-ink-primary focus:border-accent-blue focus:outline-none focus:ring-2 focus:ring-accent-blue/30"
                id="username"
                maxLength={50}
                minLength={3}
                name="username"
                pattern="[a-zA-Z0-9_.@\-]+"
                placeholder=" "
                required
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <label
                htmlFor="username"
                className="pointer-events-none absolute left-3 top-3 text-sm text-ink-secondary transition-all peer-focus:top-1 peer-focus:text-xs peer-[:not(:placeholder-shown)]:top-1 peer-[:not(:placeholder-shown)]:text-xs peer-[:focus:not(:placeholder-shown)]:top-1 peer-[:focus:not(:placeholder-shown)]:text-xs"
              >
                Correo electrónico o número de celular
              </label>
            </div>

            <div className="form-floating relative">
              <input
                aria-describedby="passwordError"
                autoComplete="current-password"
                className="form-control peer w-full rounded-lg border border-slate-200 bg-white px-3 py-3 pr-12 text-sm text-ink-primary focus:border-accent-blue focus:outline-none focus:ring-2 focus:ring-accent-blue/30"
                id="password"
                minLength={8}
                name="password"
                placeholder=" "
                required
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <label
                htmlFor="password"
                className="pointer-events-none absolute left-3 top-3 text-sm text-ink-secondary transition-all peer-focus:top-1 peer-focus:text-xs peer-[:not(:placeholder-shown)]:top-1 peer-[:not(:placeholder-shown)]:text-xs peer-[:focus:not(:placeholder-shown)]:top-1 peer-[:focus:not(:placeholder-shown)]:text-xs"
              >
                Contraseña
              </label>
              <button
                aria-label="Mostrar u ocultar contraseña"
                className="password-toggle absolute right-2 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-md text-ink-secondary transition-colors hover:bg-slate-100 hover:text-ink-primary"
                id="togglePassword"
                title="Mostrar/Ocultar contraseña"
                type="button"
                onClick={() => setShowPassword((s) => !s)}
              >
                {showPassword ? (
                  <svg
                    fill="none"
                    height="20"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                    width="20"
                    aria-hidden="true"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                    <path d="M14.12 14.12A3 3 0 1 1 9.88 9.88" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg
                    fill="none"
                    height="20"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                    width="20"
                    aria-hidden="true"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>

            <label className="flex items-center gap-2 text-sm text-ink-secondary">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-accent-blue focus:ring-accent-blue/30"
              />
              <span>Recordarme</span>
            </label>

            {error ? (
              <div
                aria-live="polite"
                className="rounded-md border border-accent-red/30 bg-accent-red/10 px-3 py-2 text-center text-sm text-accent-red"
                role="alert"
              >
                {error}
              </div>
            ) : null}

            <Button
              id="submitBtn"
              type="submit"
              loading={loading}
              className="btn btn-primary btn-login w-full"
              size="lg"
            >
              {loading ? (
                <span
                  aria-hidden="true"
                  className="spinner-border spinner-border-sm me-2"
                  id="loadingSpinner"
                  role="status"
                />
              ) : null}
              <span id="btnText">Iniciar sesión</span>
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
};
